/**
 * Knowledge Base Suggestions Component
 *
 * Displays relevant knowledge base articles based on user query.
 * Searches by title and tags, shows top 3 matches.
 */

import React, { useEffect, useState } from 'react';
import {
  Stack,
  Group,
  Text,
  Badge,
  Card,
  Loader,
  Center,
  Button,
  Box,
  Tooltip,
} from '@mantine/core';
import { IconBook, IconExternalLink } from '@tabler/icons-react';
import { api } from '../../lib/api/client';
import type { KnowledgeBaseArticle } from '../../lib/api/domains/knowledgeBase';

interface KnowledgeBaseSuggestionsProps {
  userMessage: string;
  isLoading?: boolean;
}

export function KnowledgeBaseSuggestions({
  userMessage,
  isLoading,
}: KnowledgeBaseSuggestionsProps) {
  const [suggestions, setSuggestions] = useState<KnowledgeBaseArticle[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    const searchArticles = async () => {
      if (!userMessage.trim() || userMessage.length < 3) {
        setSuggestions([]);
        return;
      }

      setIsSearching(true);
      try {
        // Search for relevant articles
        const result = await api.listArticles({
          search: userMessage,
          publishedOnly: true,
          limit: 3,
        });

        setSuggestions(result.articles);
      } catch (error) {
        console.error('Failed to search knowledge base:', error);
        setSuggestions([]);
      } finally {
        setIsSearching(false);
      }
    };

    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(searchArticles, 500);
    return () => clearTimeout(timeoutId);
  }, [userMessage]);

  if (isLoading || !userMessage.trim() || userMessage.length < 3) {
    return null;
  }

  return (
    <Box style={{ borderLeft: '1px solid var(--mantine-color-dark-5)', paddingLeft: '1rem' }}>
      <Stack gap="sm" style={{ minWidth: '280px' }}>
        <Group gap="xs">
          <IconBook size={16} />
          <Text size="sm" fw={500}>
            Ähnliche Artikel
          </Text>
        </Group>

        {isSearching ? (
          <Center style={{ minHeight: '100px' }}>
            <Loader size="sm" />
          </Center>
        ) : suggestions.length === 0 ? (
          <Text size="xs" c="dimmed" ta="center" style={{ paddingY: '1rem' }}>
            Keine Artikel gefunden
          </Text>
        ) : (
          <Stack gap="xs">
            {suggestions.map((article) => (
              <Card
                key={article.id}
                p="sm"
                radius="md"
                style={{
                  border: '1px solid var(--mantine-color-dark-5)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                className="hover-card"
              >
                <Stack gap="xs">
                  <Tooltip label={article.title} multiline maw={250}>
                    <Text
                      size="sm"
                      fw={500}
                      truncate
                      component="a"
                      href={`/knowledge-base/${article.slug}`}
                      target="_blank"
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      {article.title}
                    </Text>
                  </Tooltip>

                  <Group gap="xs" style={{ flexWrap: 'wrap' }}>
                    <Badge size="xs" variant="light">
                      {article.category}
                    </Badge>
                    {article.tags && article.tags.length > 0 && (
                      <Text size="xs" c="dimmed">
                        {article.tags.slice(0, 2).join(', ')}
                      </Text>
                    )}
                  </Group>

                  {article.viewCount > 0 && (
                    <Text size="xs" c="dimmed">
                      👁️ {article.viewCount} Aufrufe
                    </Text>
                  )}

                  <Button
                    size="xs"
                    variant="light"
                    rightSection={<IconExternalLink size={14} />}
                    component="a"
                    href={`/knowledge-base/${article.slug}`}
                    target="_blank"
                    fullWidth
                  >
                    Ansehen
                  </Button>
                </Stack>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>

      <style>{`
        .hover-card:hover {
          border-color: var(--mantine-color-cyberOrange-7);
          background-color: rgba(255, 102, 0, 0.08);
          box-shadow: 0 2px 8px rgba(255, 102, 0, 0.18);
        }
      `}</style>
    </Box>
  );
}
